import React, { useId, useRef } from "react";
import { motion } from "framer-motion";
import clsx from "clsx";


const UnderlineTabs = ({
  tabs = [],
  selectedKey,
  tab, // alias for selectedKey for compatibility
  onSelectionChange,
  handleTabClick, // alias for onSelectionChange
  onTabChange,
  className = "",
  classNames = {},
  disableAnimation = false,
  layoutId,
  ariaLabel = "Tabs",
}) => {
  const generatedId = useId();
  const indicatorId = layoutId || `underline-tabs-indicator-${generatedId}`;
  const tabListRef = useRef(null);

  const activeKey = selectedKey !== undefined ? selectedKey : tab;

  const handleSelect = (key) => {
    if (onSelectionChange) onSelectionChange(key);
    if (handleTabClick) handleTabClick(key);
    if (onTabChange) onTabChange(key);
  };

  const handleKeyDown = (e, index) => {
    const total = tabs.length;
    let nextIndex = null;

    if (e.key === "ArrowRight") {
      nextIndex = (index + 1) % total;
    } else if (e.key === "ArrowLeft") {
      nextIndex = (index - 1 + total) % total;
    } else if (e.key === "Home") {
      nextIndex = 0;
    } else if (e.key === "End") {
      nextIndex = total - 1;
    }

    if (nextIndex !== null) {
      e.preventDefault();
      const nextTab = tabs[nextIndex];
      if (nextTab && !nextTab.disabled) {
        handleSelect(nextTab.key);
        const buttons = tabListRef.current?.querySelectorAll('[role="tab"]');
        buttons?.[nextIndex]?.focus();
      }
    }
  };

  return (
    <div
      className={clsx(
        "relative w-full border-y border-gray-200 dark:border-gray-800 bg-transparent",
        classNames.base,
        className
      )}
    >
      <div
        ref={tabListRef}
        role="tablist"
        aria-label={ariaLabel}
        className={clsx(
          "flex items-center gap-2 overflow-x-auto scrollbar-hide -mb-px",
          classNames.tabList
        )}
      >
        {tabs.map((item, index) => {
          const isSelected = String(item.key) === String(activeKey);
          const isDisabled = !!item.disabled;
          const label = item.title ?? item.label ?? item.name;

          return (
            <div key={item.key} className="relative inline-flex items-center py-2">
              <button
                type="button"
                role="tab"
                id={`tab-${item.key}`}
                aria-controls={`tabpanel-${item.key}`}
                aria-selected={isSelected}
                tabIndex={isSelected ? 0 : -1}
                disabled={isDisabled}
                onClick={() => !isDisabled && handleSelect(item.key)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                className={clsx(
                  "group inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-normal select-none outline-none transition-colors duration-200 cursor-pointer whitespace-nowrap bg-transparent",
                  "focus-visible:ring-2 focus-visible:ring-secondary/30 focus-visible:rounded-sm",
                  isSelected
                    ? clsx(
                      "text-secondary dark:text-secondary-light font-semibold",
                      classNames.activeTab
                    )
                    : clsx(
                      "text-gray-500 dark:text-gray-400 hover:text-gray-400 dark:hover:text-gray-100 font-normal",
                      classNames.inactiveTab
                    ),
                  isDisabled && "opacity-40 cursor-not-allowed pointer-events-none",
                  classNames.tab
                )}
              >
                {/* Tab Content (Icon + Label + Badge) */}
                <span
                  className={clsx(
                    "inline-flex items-center gap-2 transition-transform duration-200 ease-out will-change-transform",
                    !isDisabled && !isSelected && "group-hover:-translate-y-[1.5px]"
                  )}
                >
                  {/* Tab Icon */}
                  {item.icon && (
                    <span
                      className={clsx(
                        "shrink-0 inline-flex items-center justify-center text-current transition-colors duration-200",
                        classNames.icon
                      )}
                    >
                      {React.isValidElement(item.icon) ? (
                        React.cloneElement(item.icon, {
                          ...(typeof item.icon.type !== "string" && {
                            size: item.icon.props?.size || 16,
                          }),
                          className: clsx("shrink-0 text-current", item.icon.props?.className),
                        })
                      ) : typeof item.icon === "function" ? (
                        <item.icon size={16} className="shrink-0 text-current" />
                      ) : (
                        item.icon
                      )}
                    </span>
                  )}

                  {/* Tab Label */}
                  <span className={clsx("truncate text-current transition-colors duration-200", classNames.title)}>
                    {label}
                  </span>

                  {/* Optional Badge */}
                  {item.badge != null && (
                    <span
                      className={clsx(
                        "ml-1 px-1.5 py-0.5 text-xs rounded-full font-medium",
                        isSelected
                          ? "bg-secondary-transparent text-secondary dark:text-secondary-light"
                          : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
                        classNames.badge
                      )}
                    >
                      {item.badge}
                    </span>
                  )}
                </span>
              </button>

              {/* Active Underline Indicator (Outside Button) */}
              {isSelected && (
                <>
                  {!disableAnimation ? (
                    <motion.div
                      layoutId={indicatorId}
                      className={clsx(
                        "absolute bottom-0 left-0 right-0 h-[2px] bg-secondary dark:bg-secondary-light z-10 pointer-events-none",
                        classNames.indicator || classNames.cursor
                      )}
                      transition={{
                        type: "spring",
                        stiffness: 500,
                        damping: 35,
                      }}
                    />
                  ) : (
                    <div
                      className={clsx(
                        "absolute bottom-0 left-0 right-0 h-[2px] bg-secondary dark:bg-secondary-light z-10 pointer-events-none",
                        classNames.indicator || classNames.cursor
                      )}
                    />
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UnderlineTabs;
export { UnderlineTabs, UnderlineTabs as CommonUnderlineTabs };
